import { useEffect, useState } from 'react';

const STORAGE_KEY = 'ias_recent_items';
const MAX_ITEMS = 5;

export interface RecentItem {
  id: string;
  type: 'company' | 'site' | 'report';
  label: string;
  timestamp: number;
}

const TYPE_ALIASES: Record<string, RecentItem['type']> = {
  company: 'company',
  companies: 'company',
  site: 'site',
  sites: 'site',
  report: 'report',
  reports: 'report',
};

function normalizeRecentType(value: unknown): RecentItem['type'] | null {
  if (typeof value !== 'string') return null;
  const normalized = TYPE_ALIASES[value.trim().toLowerCase()];
  return normalized ?? null;
}

function sanitizeRecentItems(raw: unknown): RecentItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item): RecentItem | null => {
      if (!item || typeof item !== 'object') return null;
      const record = item as Record<string, unknown>;
      const type = normalizeRecentType(record.type);
      const id = typeof record.id === 'string' ? record.id : null;
      const label = typeof record.label === 'string' ? record.label : null;
      const timestamp = typeof record.timestamp === 'number' ? record.timestamp : Date.now();
      if (!type || !id || label === null) return null;
      return { id, type, label, timestamp };
    })
    .filter((item): item is RecentItem => Boolean(item));
}

function parseStoredItems(stored: string | null): RecentItem[] {
  if (!stored) return [];
  try {
    const parsed = JSON.parse(stored);
    return sanitizeRecentItems(parsed);
  } catch (error) {
    console.warn('Failed to load recent items:', error);
    return [];
  }
}

/**
 * Hook to manage recent items in localStorage
 * Re-renders when recent items change via custom event
 */
export function useRecentItems() {
  const [items, setItems] = useState<RecentItem[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return parseStoredItems(stored);
  });

  // Listen for custom event when recent items change
  useEffect(() => {
    const handleUpdate = () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      setItems(parseStoredItems(stored));
    };

    window.addEventListener('recentItemsChanged', handleUpdate);
    return () => {
      window.removeEventListener('recentItemsChanged', handleUpdate);
    };
  }, []);

  return items;
}

/**
 * Add an item to recent items
 * - Deduplicates by id
 * - Moves existing items to top
 * - Limits to MAX_ITEMS
 */
export function addRecentItem(item: Omit<RecentItem, 'timestamp'>) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    let items: RecentItem[] = [];

    if (stored) {
      const parsed = JSON.parse(stored);
      items = Array.isArray(parsed) ? parsed : [];
    }

    // Remove existing item with same id (to move to top)
    items = items.filter((i) => i.id !== item.id);

    // Add new item at the beginning
    items.unshift({
      ...item,
      timestamp: Date.now(),
    });

    // Limit to MAX_ITEMS
    items = items.slice(0, MAX_ITEMS);

    // Save back to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));

    // Dispatch custom event to notify hooks
    window.dispatchEvent(new Event('recentItemsChanged'));
  } catch (error) {
    console.error('Failed to add recent item:', error);
  }
}
